import { CacheType } from '@descomplica/caching-browser';
import {
  BaseHttpGateway,
  CollaborationResult,
  RecognitionManager,
} from '@descomplica/microfrontend-core';
import { injectable } from 'tsyringe';

import { APPLICATION_REMOVED } from '@/modules/platform/admin/application/collaborations/remove-application.collaboration';
import { APPLICATION_UPDATED } from '@/modules/platform/admin/application/collaborations/update-application.collaboration';

import {
  AccountGrantsDto,
  AddTagToAccountDto,
  CreateSSORegistrationDto,
  CredentialsResultDto,
  ImpersonateInput,
  InvitationsSearchCriteria,
  Pagination,
  RemoveTagFromAccountDto,
  RequestChangePasswordDto,
  SignInWithSSODto,
  SignInWithSSOResultDto,
  SignUpWithSSODto,
  SignUpWithSSOResultDto,
  SSORegistrationDto,
  TagDto,
} from '../../application/interfaces/pegasus-io.gateway';
import {
  AccountDto,
  AddRoleToAccountDto,
  ApplicationDto,
  ChangePasswordDto,
  CheckInvitationDto,
  CreateApplicationDto,
  CreateInvitesDto,
  CreateOrganizationDto,
  CreateRoleDto,
  CreateUserDto,
  InviteDto,
  IPegasusIOGateway,
  OrganizationDto,
  RemoveRoleFromAccountDto,
  ScopeDto,
  SignInDto,
  SignInResultDto,
  SignUpDto,
  UpdateApplicationDto,
  UpdateBasicInfoDto,
  UpdateOrganizationDto,
  UpdatePasswordDto,
  UpdateRoleDto,
} from '../../application/interfaces/pegasus-io.gateway';

@injectable()
export class PegasusIOGateway extends BaseHttpGateway implements IPegasusIOGateway {
  private platformPath = '/automated-application-creation/platform';
  private platformAdminPath = '/automated-application-creation/platform/admin';
  private recognitionPath = '/recognition';
  private recognitionAdminPath = '/recognition/admin';

  constructor(private readonly recognitionManager: RecognitionManager) {
    super();
  }

  // invites related
  async createInvites(invites: CreateInvitesDto): Promise<CollaborationResult<InviteDto[]>> {
    const { data } = await this.axios.post(`${this.recognitionAdminPath}/invitations`, invites);
    return data;
  }

  // organization related
  async createOrganization(
    organization: CreateOrganizationDto,
  ): Promise<CollaborationResult<OrganizationDto>> {
    const { data } = await this.axios.post(`${this.platformAdminPath}/organizations`, organization);
    return data;
  }

  async getOrganizations(): Promise<CollaborationResult<OrganizationDto[]>> {
    const { data } = await this.axios.get(`${this.platformAdminPath}/organizations`);
    return data;
  }

  async getOrganization(guid: string): Promise<CollaborationResult<OrganizationDto>> {
    const { data } = await this.axios.get(`${this.platformAdminPath}/organizations/${guid}`);
    return data;
  }

  async updateOrganization(
    organization: UpdateOrganizationDto,
  ): Promise<CollaborationResult<OrganizationDto>> {
    const { data } = await this.axios.patch(
      `${this.platformAdminPath}/organizations/${organization.guid}`,
      organization,
    );

    return data;
  }

  async removeOrganization(guid: string): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.delete(`${this.platformAdminPath}/organizations/${guid}`);
    return data;
  }

  // applications related
  async createApplication(
    application: CreateApplicationDto,
  ): Promise<CollaborationResult<ApplicationDto>> {
    const { data } = await this.axios.post(`${this.platformAdminPath}/applications`, application);
    return data;
  }

  async getApplications(): Promise<CollaborationResult<ApplicationDto[]>> {
    const { data } = await this.axios.get(`${this.platformAdminPath}/applications`);
    return data;
  }

  async getApplication(guid: string): Promise<CollaborationResult<ApplicationDto>> {
    const { data } = await this.axios.get(`${this.platformAdminPath}/applications/${guid}`);
    return data;
  }

  async updateApplication(
    input: UpdateApplicationDto,
  ): Promise<CollaborationResult<ApplicationDto>> {
    const { scope, application } = input;

    const { data } = await this.axios.patch(
      `${this.platformAdminPath}/applications/${application.guid}`,
      application,
    );

    if (data !== APPLICATION_UPDATED) {
      await this.axios.put(`${this.recognitionAdminPath}/scopes/${scope.guid}`, scope);
    }

    return data;
  }

  async removeApplication(guid: string, scopeGuid: string): Promise<CollaborationResult<void>> {
    const { data: applicationData } = await this.axios.delete(
      `${this.platformAdminPath}/applications/${guid}`,
    );

    if (applicationData.identifier === APPLICATION_REMOVED) {
      await this.axios.delete(`${this.recognitionAdminPath}/scopes/${scopeGuid}`);
    }

    return applicationData;
  }

  // invite related
  async checkInvitation(dto: CheckInvitationDto) {
    const { data } = await this.axios.post(`${this.recognitionPath}/invitations/${dto.token}`);
    return data;
  }

  async getInvitations(searchCriteria?: InvitationsSearchCriteria, pagination?: Pagination) {
    const qsObject = {
      ...searchCriteria,
      ...pagination,
    };

    const queryString = Object.keys(qsObject)
      .map((key) => `${key}=${qsObject[key]}`)
      .join('&');

    const { data } = await this.axios.get(
      `${this.recognitionAdminPath}/invitations?${queryString}`,
    );

    return data;
  }

  async getUserApplications(): Promise<CollaborationResult<ApplicationDto[]>> {
    const { data } = await this.axios.get(`${this.platformPath}/account/applications`);
    return data;
  }

  async signUp(dto: SignUpDto) {
    const { data } = await this.axios.post(`${this.recognitionPath}/signup`, dto);
    return data;
  }

  async signIn(dto: SignInDto): Promise<CollaborationResult<SignInResultDto>> {
    const { data } = await this.axios.post(`${this.recognitionPath}/signin`, dto);
    return data;
  }

  async getRoles() {
    const { data } = await this.axios.get(`${this.recognitionAdminPath}/roles`);
    return data;
  }

  async createRole(dto: CreateRoleDto) {
    const { data } = await this.axios.post(`${this.recognitionAdminPath}/roles`, dto);
    return data;
  }

  async removeRole(roleGuid: string): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.delete(`${this.recognitionAdminPath}/roles/${roleGuid}`);
    return data;
  }

  async updateRole(dto: UpdateRoleDto): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.put(`${this.recognitionAdminPath}/roles/${dto.guid}`, dto);
    return data;
  }

  async getAccountsByScope(scopeGuid: string): Promise<CollaborationResult<AccountDto[]>> {
    const { data } = await this.axios.get(
      `${this.recognitionAdminPath}/accounts?scope=${scopeGuid}`,
    );
    return data;
  }

  async createAccount(dto: CreateUserDto): Promise<CollaborationResult<AccountDto>> {
    const recognitionScopeGuid = process.env.REACT_APP_RECOGNITION_DESCOMPLICA_IO_SCOPE;
    if (!dto.scopeGuids.includes(recognitionScopeGuid)) {
      dto.scopeGuids.push(recognitionScopeGuid);
    }

    const { data } = await this.axios.post(`${this.recognitionAdminPath}/accounts`, dto);
    return data;
  }

  async getAccountByRegistrationCode(
    registrationCode: string,
  ): Promise<CollaborationResult<AccountDto>> {
    const { data } = await this.axios.get(
      `${this.recognitionAdminPath}/accounts/${registrationCode}`,
    );
    return data;
  }

  async getScopes(): Promise<CollaborationResult<ScopeDto[]>> {
    const { data } = await this.axios.get(`${this.recognitionAdminPath}/scopes`);
    return data;
  }

  async addRoleToAccount(dto: AddRoleToAccountDto): Promise<CollaborationResult<AccountDto>> {
    const { data } = await this.axios.post(
      `${this.recognitionAdminPath}/accounts/${dto.accountRegistrationCode}/roles`,
      dto,
    );
    return data;
  }

  async removeRoleFromAccount(dto: RemoveRoleFromAccountDto): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.delete(
      `${this.recognitionAdminPath}/accounts/${dto.accountGuid}/roles/${dto.roleGuid}/scopes/${dto.scopeGuid}`,
    );
    return data;
  }

  async updateBasicInfo(dto: UpdateBasicInfoDto): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.patch(`${this.recognitionPath}/account/basic-info`, dto);
    return data;
  }

  async changePassword(dto: ChangePasswordDto): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.put(`${this.recognitionPath}/account/password`, dto);
    return data;
  }

  async getAccount(): Promise<CollaborationResult<AccountDto>> {
    const { data } = await this.axios.get(`${this.recognitionPath}/account`);
    return data;
  }

  async getAccountGrants(): Promise<CollaborationResult<AccountGrantsDto>> {
    const { data } = await this.axios.get(`${this.platformPath}/account/grants`);
    return data;
  }

  async updatePassword(dto: UpdatePasswordDto): Promise<CollaborationResult<SignInResultDto>> {
    const { data } = await this.axios.post(
      `${this.recognitionPath}/update-password/${dto.token}`,
      dto,
    );
    return data;
  }

  async requestChangePassword(dto: RequestChangePasswordDto): Promise<CollaborationResult<void>> {
    const { data } = await this.axios.post(`${this.recognitionPath}/reset-password`, dto);
    return data;
  }

  async impersonate(input: ImpersonateInput): Promise<CollaborationResult<CredentialsResultDto>> {
    const result = await this.axios.post(
      `${this.recognitionAdminPath}/accounts/${input.registrationCode}/impersonation`,
      input,
    );
    return result.data;
  }

  async getAccountTags(registrationCode: string): Promise<CollaborationResult<TagDto[]>> {
    const result = await this.axios.get(
      `${this.recognitionAdminPath}/accounts/${registrationCode}/tags`,
    );
    return result.data;
  }

  async addTagToAccount(dto: AddTagToAccountDto): Promise<CollaborationResult<TagDto>> {
    const result = await this.axios.post(
      `${this.recognitionAdminPath}/accounts/${dto.accountRegistrationCode}/tags`,
      dto,
    );
    return result.data;
  }

  async removeTagFromAccount(dto: RemoveTagFromAccountDto): Promise<CollaborationResult<void>> {
    const result = await this.axios.delete(
      `${this.recognitionAdminPath}/accounts/${dto.accountRegistrationCode}/tags/${dto.tagName}/scope/${dto.scopeGuid}`,
    );
    return result.data;
  }

  /* SSO */

  async getSSORegistrations(): Promise<CollaborationResult<SSORegistrationDto[]>> {
    const result = await this.axios.get(`${this.recognitionAdminPath}/sso/registrations`);
    return result.data;
  }

  async createSSORegistration(
    dto: CreateSSORegistrationDto,
  ): Promise<CollaborationResult<SSORegistrationDto>> {
    const result = await this.axios.post(`${this.recognitionAdminPath}/sso/registrations`, dto);
    return result.data;
  }

  async signInWithSSO(dto: SignInWithSSODto): Promise<CollaborationResult<SignInWithSSOResultDto>> {
    const result = await this.axios.post(`${this.recognitionPath}/sso/signin`, dto);
    return result.data;
  }

  async signUpWithSSO(dto: SignUpWithSSODto): Promise<CollaborationResult<SignUpWithSSOResultDto>> {
    const result = await this.axios.post(`${this.recognitionPath}/sso/signup`, dto);
    return result.data;
  }

  /* Other configurations */

  getEndpoint() {
    return `${process.env.REACT_APP_PEGASUS_IO_URL}`;
  }

  getBearerToken() {
    return this.recognitionManager.getAccessToken();
  }

  getHeaders() {
    const headers = {};
    headers['Cache-Control'] = 'no-cache';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = '0';
    return headers;
  }

  getCacheNamespace() {
    return 'pegasus-io';
  }

  getCacheType() {
    return CacheType.LOCALSTORAGE;
  }
}
